import React, { useState, useEffect, useRef } from 'react';
import { translate } from 'react-multi-lang';
import { Table, Input, Button, Space, Checkbox } from 'antd';
import { SearchOutlined, FileExcelOutlined } from '@ant-design/icons';
import { AllHtmlEntities } from 'html-entities';

import ContainerPagina from '../../../ContainerPagina';
import TitolPagina from '../../../TitolPagina';
import { Api, HtmlStrings } from '../../../../helpers';


const entities = new AllHtmlEntities();

const AlumnesExcursio = ({ id_producte, t }) => {
  const [dades, setDades] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filtresClasses, setFiltresClasses] = useState([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');

  const searchInput = useRef(null);

  useEffect(() => {
    buscarDades();
  }, [id_producte]);

  const buscarDades = async () => {
    setLoading(true);

    if (!id_producte) {
      console.warn("No s'ha passat cap id_producte al component");
      setLoading(false);
      return;
    }

    const res = await Api.get(`/excursions/compratvsnocomprat/${id_producte}`);

    if (res?.result?.success === 1) {
      const alumnes = res.data.alumnes;

      // 1. Ordenem els alumnes
      alumnes.sort((a, b) => b.id - a.id);

      // 2. Extraiem les classes úniques per generar el filtre
      const classesUniques = [];
      const classesMap = new Map();

      alumnes.forEach((alumne) => {
        // Comprovem que l'alumne tingui classe i que no l'hàgim afegit ja al Map
        if (alumne.id_classe && alumne.classe && !classesMap.has(alumne.id_classe)) {
          classesMap.set(alumne.id_classe, true);
          classesUniques.push({
            text: entities.decode(alumne.classe),
            value: alumne.id_classe
          });
        }
      });

      // Ordenem alfabèticament els filtres resultants perquè quedin bé al menú
      classesUniques.sort((a, b) => a.text.localeCompare(b.text));

      // 3. Guardem als estats
      setFiltresClasses(classesUniques);
      setDades(alumnes);
    }
    setLoading(false);
  };

  const arreglarTxt4Search = (text) => {
    return text.trim().toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, "");
  };

  const handleSearch = (selectedKeys, confirm, dataIndex) => {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  };

  const handleReset = (clearFilters) => {
    clearFilters();
    setSearchText('');
  };

  const exportarCSV = () => {
    // 1. Definim les capçaleres de les columnes
    const capcaleres = ['Id', 'Classe', 'Alumne', 'Comprat'];

    // 2. Extraiem i netegem les dades de cada fila
    const files = dades.map(alumne => [
      alumne.id_alumne,
      entities.decode(alumne.classe || ''),
      entities.decode(alumne.alumne || ''),
      alumne.comprat || ''
    ]);

    // 3. Unim les dades amb punt i coma (l'Excel europeu ho prefereix en lloc de la coma)
    const contingutCSV = [
      capcaleres.join(';'),
      ...files.map(fila => fila.join(';'))
    ].join('\n');

    // 4. Creem el fitxer (afegim "\ufeff" al principi perquè Excel llegeixi bé els accents en català)
    const blob = new Blob(["\ufeff", contingutCSV], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);

    // 5. Forcem la descàrrega
    const link = document.createElement('a');
    link.href = url;
    link.setAttribute('download', `alumnes_excursio_${id_producte}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };


  const getClasseFilterProps = () => ({
  filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
    <div style={{ padding: 8 }}>
      <div style={{ maxHeight: '250px', overflowY: 'auto', marginBottom: '10px' }}>
        <Checkbox.Group
          style={{ display: 'flex', flexDirection: 'column', gap: '8px', padding: '4px' }}
          options={filtresClasses.map(f => ({ label: f.text, value: f.value }))}
          value={selectedKeys}
          onChange={(checkedValues) => setSelectedKeys(checkedValues)}
        />
      </div>
      <Space>
        <Button
          type="primary"
          onClick={() => confirm()}
          size="small"
          style={{ width: 90 }}
        >
          Filtrar
        </Button>
        <Button
          onClick={() => {
            clearFilters();
            confirm(); // Apliquem el reset immediatament
          }}
          size="small"
          style={{ width: 90 }}
        >
          Reset
        </Button>
      </Space>
    </div>
  ),
  filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
  onFilter: (value, record) => record.id_classe === value,
});

  const getColumnSearchProps = (dataIndex, nom) => ({
    filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters }) => (
      <div style={{ padding: 8 }}>
        <Input
          ref={searchInput}
          placeholder={`${t('generic.cercar')} ${nom}`}
          value={selectedKeys[0]}
          onChange={e => setSelectedKeys(e.target.value ? [e.target.value] : [])}
          onPressEnter={() => handleSearch(selectedKeys, confirm, dataIndex)}
          style={{ width: 188, marginBottom: 8, display: 'block' }}
        />
        <Space>
          <Button
            type="primary"
            onClick={() => handleSearch(selectedKeys, confirm, dataIndex)}
            icon={<SearchOutlined />}
            size="small"
            style={{ width: 90 }}
          >
            {t('generic.cercar')}
          </Button>
          <Button onClick={() => handleReset(clearFilters)} size="small" style={{ width: 90 }}>
            Reset
          </Button>
        </Space>
      </div>
    ),
    filterIcon: filtered => <SearchOutlined style={{ color: filtered ? '#1890ff' : undefined }} />,
    onFilter: (value, record) =>
      record[dataIndex] ? arreglarTxt4Search(entities.decode(record[dataIndex].toString())).includes(arreglarTxt4Search(value)) : '',
    onFilterDropdownVisibleChange: visible => {
      if (visible) {
        setTimeout(() => searchInput.current?.select(), 100);
      }
    },
    render: text => (entities.decode(text)),
  });

  const columns = [
    {
      title: "Id",
      key: "id_alumne",
      sorter: {
        compare: (a, b) => a.id_alumne - b.id_alumne,
        multiple: 0
      },
      render: (text, record) => (<span>{record.id_alumne}</span>)
    },
    {
      title: t('alumnes.classe'),
      key: "classe",
      dataIndex: 'classe',
      render: (text, record) => (entities.decode(record.classe)),
      sorter: {
        compare: (a, b) => entities.decode(a.classe).localeCompare(entities.decode(b.classe)),
        multiple: 0
      },
      // Cridem el nostre filtre personalitzat
      ...getClasseFilterProps()
    },
    {
      title: t('alumnes.nom'),
      key: "alumne",
      dataIndex: 'alumne',
      render: (text, record) => (entities.decode(record.alumne)),
      sorter: {
        compare: (a, b) => HtmlStrings.normalize(entities.decode(a.alumne)).localeCompare(HtmlStrings.normalize(entities.decode(b.alumne))),
        multiple: 0,
      },
      ...getColumnSearchProps('nom', t('alumnes.nom'))
    },
    {
      title: t('menu.Comandes'),
      key: "comprat",
      dataIndex: "comprat"
    }

  ];

  return (
    <ContainerPagina xl={24}>
      {/*<TitolPagina txt={t('menu.Alumnes')} />*/}
      <div style={{ marginTop: '0rem', marginLeft: '3rem', marginRight: '3rem' }}>
        <div style={{ position: 'relative' }}>
          <div style={{ position: 'absolute', top: 16, left: 0, zIndex: 2 }}>

            <Button
              type="primary"
              size="small"
              style={{backgroundColor: '#217346', borderColor: '#217346'}}
              icon={<FileExcelOutlined />}
              onClick={exportarCSV}
              disabled={dades.length === 0} // El desactivem si no hi ha dades
            >
              Export CSV
            </Button>

          </div>
          <Table
            size="small"
            columns={columns}
            dataSource={dades}
            rowKey="id_alumne"
            loading={loading}
            pagination={{
              pageSize: 20,
              position: ['topRight', 'bottomRight']
            }}
            bordered
            rowClassName={(record) => record.comprat === 'NO' ? 'fila-vermella' : ''}
          />
        </div>
      </div>
    </ContainerPagina>
  );
};

export default translate(AlumnesExcursio);
